/** Small dependency-free JSON boundary parser. No coercion or mutation. */
export interface ValidationIssue { path: string; code: string; message: string }
export type ValidationResult<T> =
  | { ok: true; value: T }
  | { ok: false; issues: ValidationIssue[] };
export interface Schema<T> { read(value: unknown, path: string, issues: ValidationIssue[]): T | undefined }
const issue = (issues: ValidationIssue[], path: string, code: string, message: string) => { issues.push({ path, code, message }); };
export function parse<T>(schema: Schema<T>, value: unknown): ValidationResult<T> {
  const issues: ValidationIssue[] = [];
  const parsed = schema.read(value, '$', issues);
  return issues.length ? { ok: false, issues } : { ok: true, value: parsed as T };
}
export function text(pattern?: RegExp): Schema<string> {
  return { read(value, path, issues) {
    if (typeof value !== 'string' || !value.trim() || value !== value.trim() || (pattern && !pattern.test(value))) {
      issue(issues, path, 'invalid_string', 'Expected a nonempty, trimmed string in the required format.'); return;
    }
    return value;
  } };
}
export function number(min: number, max: number, integer = true): Schema<number> {
  return { read(value, path, issues) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max || (integer && !Number.isInteger(value))) {
      issue(issues, path, 'invalid_number', 'Expected a finite number in the required range.'); return;
    }
    return value;
  } };
}
export const boolean: Schema<boolean> = { read(value, path, issues) {
  if (typeof value !== 'boolean') { issue(issues, path, 'invalid_boolean', 'Expected a boolean.'); return; }
  return value;
} };
export function enumeration<const T extends string>(values: readonly T[]): Schema<T> {
  return { read(value, path, issues) {
    if (typeof value !== 'string' || !values.includes(value as T)) { issue(issues, path, 'invalid_enum', `Expected one of: ${values.join(', ')}.`); return; }
    return value as T;
  } };
}
export function nullable<T>(schema: Schema<T>): Schema<T | null> {
  return { read: (value, path, issues) => value === null ? null : schema.read(value, path, issues) };
}
export function optional<T>(schema: Schema<T>): Schema<T | undefined> {
  return { read: (value, path, issues) => value === undefined ? undefined : schema.read(value, path, issues) };
}
export function array<T>(schema: Schema<T>): Schema<T[]> {
  return { read(value, path, issues) {
    if (!Array.isArray(value)) { issue(issues, path, 'invalid_array', 'Expected an array.'); return; }
    // Array.from also validates holes rather than skipping them.
    return Array.from(value, (item, i) => schema.read(item, `${path}[${i}]`, issues)) as T[];
  } };
}
export function object<T extends object>(fields: { [K in keyof T]-?: Schema<T[K]> }): Schema<T> {
  return { read(value, path, issues) {
    if (value === null || typeof value !== 'object' || Array.isArray(value) || ![Object.prototype, null].includes(Object.getPrototypeOf(value))) {
      issue(issues, path, 'invalid_object', 'Expected a plain JSON object.'); return;
    }
    const input = value as Record<string, unknown>;
    for (const key of Object.keys(input)) if (!Object.hasOwn(fields, key)) issue(issues, `${path}.${key}`, 'unknown_field', 'Unknown field.');
    const output: Record<string, unknown> = {};
    for (const [key, schema] of Object.entries(fields) as [string, Schema<unknown>][]) {
      const item = schema.read(Object.hasOwn(input, key) ? input[key] : undefined, `${path}.${key}`, issues);
      if (item !== undefined) output[key] = item;
    }
    return output as T;
  } };
}
export function refine<T>(schema: Schema<T>, check: (value: T) => boolean, code: string, message: string): Schema<T> {
  return { read(value, path, issues) {
    const before = issues.length;
    const parsed = schema.read(value, path, issues);
    if (issues.length === before && !check(parsed as T)) issue(issues, path, code, message);
    return parsed;
  } };
}
export const timestamp = refine(text(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/), value => {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) && date.toISOString() === value;
}, 'invalid_timestamp', 'Expected a valid UTC ISO timestamp with milliseconds.');
